import plur from 'plur';
import { isEmptyString } from '@/utils/string';
import { ConnectionStatus, DataFlow, IntegrationCategory } from '../../catalog/enums';
import { IntegrationIDs, IntegrationNames } from '../../catalog/ids';
import { getStaticIntegrations } from '../../catalog/static-integrations';
import type { SynthesizedIntegration } from '../../directory/types';
import { toIntegrationConnection, type ConnectionInput } from './to-connection';
import type {
  AdAudienceSource,
  ConnectionSources,
  ConnectionsConfig,
  FormSource,
  IntegrationConnection,
  SqlImportRowSource,
  SqlImportSyncSource,
} from './types';

/** A parser returns the per-connection field overrides, or `undefined` to defer to the base entry. */
export type ParsingResult = Partial<IntegrationConnection>[] | undefined;
type JourneysParser = (
  i: SynthesizedIntegration,
  sources: ConnectionSources,
  config: ConnectionsConfig,
) => ParsingResult;

/** Form-type → the `:type` segment of the form-details route. */
const FORM_URL_TYPE: Record<string, string> = {
  custom: 'custom',
  typeform: 'typeform',
  jotform: 'jotform',
  facebook: 'facebook',
};

interface SqlSyncsSummary {
  hasActive: boolean;
  latestFailedImport: SqlImportRowSource | undefined;
  lastImportActivity: number;
  lastSyncedAt: number;
  /** `undefined` when the database has no syncs at all, so no date is added. */
  earliestCreatedAt: number | undefined;
}

/** A sync's newest import, or `undefined` when it has never run one. */
function mostRecentImport(imports: readonly SqlImportRowSource[]): SqlImportRowSource | undefined {
  if (imports.length === 0) {
    return undefined;
  }
  return imports.reduce((latest, imp) => (imp.createdAt > latest.createdAt ? imp : latest));
}

/** Whichever of the running best and the candidate is the newer failed import. */
function laterFailedImport(
  current: SqlImportRowSource | undefined,
  candidate: SqlImportRowSource,
): SqlImportRowSource | undefined {
  if (!candidate.isFailed) {
    return current;
  }
  return current == null || candidate.createdAt > current.createdAt ? candidate : current;
}

/** Fold a database's syncs into the activity/failure summary the status derives from. */
function summarizeSqlSyncs(syncs: readonly SqlImportSyncSource[]): SqlSyncsSummary {
  const summary: SqlSyncsSummary = {
    hasActive: false,
    latestFailedImport: undefined,
    lastImportActivity: 0,
    lastSyncedAt: 0,
    earliestCreatedAt: undefined,
  };
  for (const sync of syncs) {
    if (sync.createdAt != null) {
      summary.earliestCreatedAt =
        summary.earliestCreatedAt == null ? sync.createdAt : Math.min(summary.earliestCreatedAt, sync.createdAt);
    }
    summary.lastSyncedAt = Math.max(summary.lastSyncedAt, sync.lastSyncedAt);
    if (!sync.active) {
      continue;
    }
    summary.hasActive = true;
    const mostRecent = mostRecentImport(sync.imports);
    if (mostRecent == null) {
      continue;
    }
    summary.lastImportActivity = Math.max(
      summary.lastImportActivity,
      mostRecent.createdAt ?? 0,
      mostRecent.endedAt ?? 0,
    );
    summary.latestFailedImport = laterFailedImport(summary.latestFailedImport, mostRecent);
  }
  return summary;
}

/** The status of a database with at least one active sync (fully vs partially failed vs healthy). */
function activeSyncStatus(
  latestFailedImport: SqlImportRowSource | undefined,
): Pick<IntegrationConnection, 'status' | 'statusMessage'> {
  if (latestFailedImport == null) {
    return { status: ConnectionStatus.connected };
  }
  return {
    status: latestFailedImport.isPartiallyFailed ? ConnectionStatus.needsAttention : ConnectionStatus.error,
    statusMessage: latestFailedImport.error,
  };
}

function parseSQLImport(i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  const attributes: Partial<IntegrationConnection> = { dataFlow: DataFlow.in };
  // Some SQL Import integration ids carry an `-in` suffix (redshift / bigquery / snowflake).
  const dbId = i.id.replace('-in', '');
  const syncs = sources.sqlImportsByDatabase?.[dbId];
  if (syncs == null) {
    return [attributes];
  }

  const summary = summarizeSqlSyncs(syncs);
  if (summary.hasActive) {
    Object.assign(attributes, activeSyncStatus(summary.latestFailedImport));
  } else {
    attributes.status = ConnectionStatus.disabled;
    attributes.statusMessage =
      'No active imports found for this database. Enable at least one import to activate this connection.';
  }
  attributes.lastActivity = summary.lastSyncedAt > 0 ? summary.lastSyncedAt : summary.lastImportActivity;
  attributes.createdAt = summary.earliestCreatedAt;
  return [attributes];
}

function parseWarehouseSync(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  const sync = sources.warehouseSync;
  if (sync == null) {
    return undefined;
  }
  let status: ConnectionStatus = ConnectionStatus.connected;
  let statusMessage: string | undefined;
  if (sync.isPaused) {
    status = ConnectionStatus.disabled;
    statusMessage = 'All exports are currently disabled. Enable at least one export to activate this connection.';
  }
  return [
    { dataFlow: DataFlow.out, createdAt: sync.createdAt, lastActivity: sync.lastActivity, status, statusMessage },
  ];
}

function parseFacebookLeadAds(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  const fb = sources.facebookLeadAds;
  if (!fb.enabled) {
    return undefined;
  }
  const lastActivity = Math.max(0, ...fb.connectedFormsLastActivity);
  let status: ConnectionStatus = ConnectionStatus.connected;
  let statusMessage: string | undefined;
  if (fb.connectedFormsLastActivity.length === 0) {
    status = ConnectionStatus.disabled;
    statusMessage = 'No forms connected. Connect at least one form to activate this connection.';
  }
  return [{ createdAt: fb.credentialCreatedAt, lastActivity, status, statusMessage, dataFlow: DataFlow.in }];
}

function parseDataOut(i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  const dataOut = sources.dataOut[i.id];
  if (dataOut == null) {
    return undefined;
  }
  const { createdAt, lastActivityAt: lastActivity, enabled, events } = dataOut;
  let status: ConnectionStatus = ConnectionStatus.connected;
  let statusMessage: string | undefined;
  if (!enabled) {
    if (events == null || events.length > 0) {
      status = ConnectionStatus.disabled;
      statusMessage = 'This connection has been disabled.';
    } else {
      status = ConnectionStatus.error;
      statusMessage = 'No events selected. Enable at least one event to send out to activate this connection.';
    }
  }
  return [
    {
      dataFlow: DataFlow.out,
      createdAt,
      lastActivity,
      status,
      statusMessage,
      path: 'integrations.data_out.type',
      dynamicSegment: i.id,
    },
  ];
}

function parseSegmentOut(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  const segmentOut = sources.segmentOut;
  if (segmentOut == null) {
    return undefined;
  }
  let status: ConnectionStatus = ConnectionStatus.connected;
  let statusMessage: string | undefined;
  if (segmentOut.enabled === false) {
    status = ConnectionStatus.disabled;
    statusMessage = 'This connection has been disabled.';
  }
  return [
    {
      dataFlow: DataFlow.out,
      createdAt: segmentOut.createdAt,
      lastActivity: segmentOut.lastActivity,
      status,
      statusMessage,
    },
  ];
}

function parseMParticleOut(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  const mparticle = sources.mparticle;
  if (mparticle == null) {
    return undefined;
  }
  let status: ConnectionStatus = ConnectionStatus.connected;
  let statusMessage: string | undefined;
  if (mparticle.enabled === false) {
    status = ConnectionStatus.disabled;
    statusMessage = 'This connection has been disabled.';
  }
  return [
    {
      dataFlow: DataFlow.out,
      createdAt: mparticle.createdAt,
      lastActivity: mparticle.lastActivityAt,
      status,
      statusMessage,
    },
  ];
}

function parseAdAudiences(i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  const adNetworkType = i.id;
  const adAudiences = sources.adAudiences;
  if (adAudiences == null) {
    return undefined;
  }
  const audiences = adAudiences[adNetworkType];
  if (audiences == null) {
    return undefined;
  }

  return audiences.map((audience: AdAudienceSource) => {
    const {
      id,
      name,
      state,
      adNetworkName,
      credentialExpired,
      createdAt,
      lastSynced: lastActivity,
      lastError,
    } = audience;
    let status: ConnectionStatus = ConnectionStatus.connected;
    let statusMessage: string | undefined;

    if (credentialExpired) {
      status = ConnectionStatus.error;
      statusMessage = `Reconnect your account to continue syncing segments to ${adNetworkName}.`;
    } else if (['error', 'failed'].includes(state)) {
      status = ConnectionStatus.error;
      statusMessage = lastError;
    } else if (state === 'paused') {
      status = ConnectionStatus.disabled;
      statusMessage = 'This audience has been paused.';
    }

    return {
      id: `${adNetworkName}_${id}`,
      type: adNetworkType,
      name: name.length > 0 ? name : adNetworkName,
      dataFlow: DataFlow.out,
      createdAt,
      lastActivity,
      status,
      statusMessage,
    };
  });
}

function parseCustomerIoAPI(
  _i: SynthesizedIntegration,
  sources: ConnectionSources,
  config: ConnectionsConfig,
): ParsingResult {
  if (config.areAutoTrackSourcesEnabled) {
    // With auto-track / passthrough on, the Track API is represented by the
    // pipelines auto-track group instead — hide the standalone card.
    return undefined;
  }
  const apiKeys = sources.apiCredentials;
  if (apiKeys.length === 0) {
    return undefined;
  }
  const usedKeys = apiKeys.filter((key) => key.lastUsedAt > 0);
  if (usedKeys.length === 0) {
    return undefined;
  }
  const lastActivity = Math.max(...usedKeys.map((key) => key.lastUsedAt));
  const total = apiKeys.length;
  const createdAtValues = apiKeys.filter((key) => key.createdAt > 0).map((key) => key.createdAt);
  const createdAt = createdAtValues.length > 0 ? Math.min(...createdAtValues) : undefined;

  return [
    {
      name: 'Customer.io API: Track',
      dataFlow: DataFlow.in,
      // oxlint-disable-next-line local-rules/enforce-generate-path -- Hydra API credentials hand-off (used verbatim by createJourneysHref), not a Journeys router path
      path: `/workspaces/${config.workspaceId}/settings/api_credentials`,
      subtitle: `${total} API ${plur('key', total)} available`,
      status: ConnectionStatus.connected,
      lastActivity,
      createdAt,
    },
  ];
}

function parseInApp(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  if (!sources.enabledActions.includes('in_app')) {
    return undefined;
  }
  return [{ dataFlow: DataFlow.channel, status: ConnectionStatus.connected, createdAt: sources.inAppCreated }];
}

function parsePush(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  if (!sources.enabledActions.includes('push')) {
    return undefined;
  }
  const { platforms } = sources.push;
  if (platforms.length === 0) {
    return undefined;
  }
  const isIosConfigured = platforms.includes('ios');
  const isAndroidConfigured = platforms.includes('android');
  if (!isIosConfigured && !isAndroidConfigured) {
    return undefined;
  }
  return [
    {
      name: 'Mobile Push',
      dataFlow: DataFlow.channel,
      status: ConnectionStatus.connected,
      createdAt: sources.push.createdAt,
    },
  ];
}

function parseSlack(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  if (!sources.enabledActions.includes('slack')) {
    return undefined;
  }
  // Inverted guard, ported verbatim: the channel shows only when the credential
  // fetch resolved to exactly null (created-at is therefore always undefined).
  if (!sources.slackCredentialIsNull) {
    return undefined;
  }
  return [{ dataFlow: DataFlow.channel, status: ConnectionStatus.connected, createdAt: undefined }];
}

function parseTwilio(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  if (!sources.enabledActions.includes('twilio')) {
    return undefined;
  }
  if (!sources.twilioCredentialIsNull) {
    return undefined;
  }
  return [{ dataFlow: DataFlow.channel, status: ConnectionStatus.connected, createdAt: undefined }];
}

function parseEmail(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  if (!sources.emailSetupComplete) {
    return undefined;
  }
  return [{ dataFlow: DataFlow.channel, status: ConnectionStatus.connected }];
}

function parseUrlParams(_i: SynthesizedIntegration, sources: ConnectionSources): ParsingResult {
  if (!sources.urlParams.enabled) {
    return undefined;
  }
  return [{ dataFlow: DataFlow.channel, status: ConnectionStatus.connected, createdAt: sources.urlParams.created }];
}

/**
 * Per-integration parsers keyed by integration id. Each hydrates a synthesized
 * entry into connection field overrides. Ported from the Ember overview
 * controller's `journeysParsers`.
 */
export const journeysParsers: Record<string, JourneysParser> = {
  [IntegrationIDs.customerioAPI]: parseCustomerIoAPI,
  [IntegrationIDs.gcs]: parseWarehouseSync,
  [IntegrationIDs.amazonS3]: parseWarehouseSync,
  [IntegrationIDs.azureBlob]: parseWarehouseSync,
  [IntegrationIDs.mysql]: parseSQLImport,
  [IntegrationIDs.postgres]: parseSQLImport,
  [IntegrationIDs.sqlserver]: parseSQLImport,
  [IntegrationIDs.bigQueryIn]: parseSQLImport,
  [IntegrationIDs.redshiftIn]: parseSQLImport,
  [IntegrationIDs.snowflakeIn]: parseSQLImport,
  [IntegrationIDs.amplitude]: parseDataOut,
  [IntegrationIDs.mixpanel]: parseDataOut,
  [IntegrationIDs.segmentOut]: parseSegmentOut,
  [IntegrationIDs.mparticleOut]: parseMParticleOut,
  [IntegrationIDs.adwords]: parseAdAudiences,
  [IntegrationIDs.facebook]: parseAdAudiences,
  [IntegrationIDs.facebookLeadAds]: parseFacebookLeadAds,
  [IntegrationIDs.inApp]: parseInApp,
  [IntegrationIDs.mobile]: parsePush,
  [IntegrationIDs.slack]: parseSlack,
  [IntegrationIDs.email]: parseEmail,
  [IntegrationIDs.googleAnalytics]: parseUrlParams,
  [IntegrationIDs.twilio]: parseTwilio,
};

/** Integration ids whose parser returning `undefined` drops the connection entirely. */
const DROP_WHEN_UNRESOLVED = new Set<string>([
  IntegrationIDs.customerioAPI,
  IntegrationIDs.inApp,
  IntegrationIDs.email,
  IntegrationIDs.mobile,
  IntegrationIDs.slack,
  IntegrationIDs.twilio,
]);

/** Run the id's parser (if any) and materialize the resulting connection(s). */
export function parseJourneysIntegration(
  i: SynthesizedIntegration,
  sources: ConnectionSources,
  config: ConnectionsConfig,
): IntegrationConnection[] {
  const parser = journeysParsers[i.id];
  let result: ParsingResult;
  if (parser != null) {
    result = parser(i, sources, config);
    if (DROP_WHEN_UNRESOLVED.has(i.id) && result === undefined) {
      return [];
    }
  }
  if (result == null) {
    return [toIntegrationConnection(i)];
  }
  return result.map((override) => toIntegrationConnection(i, override));
}

/* ------------------------------------------------------------------------- */
/* Forms + reporting webhooks (parsed separately, not via journeysParsers)    */
/* ------------------------------------------------------------------------- */

const CUSTOM_FORMS_INTEGRATION: ConnectionInput = {
  id: IntegrationIDs.customForms,
  icon: 'form',
  name: IntegrationNames.customForms,
  category: IntegrationCategory.leadGeneration,
};

function parseForm(integration: ConnectionInput, form: FormSource): IntegrationConnection {
  let status: ConnectionStatus = ConnectionStatus.connected;
  let statusMessage: string | undefined;
  if (form.hasError) {
    status = ConnectionStatus.error;
    statusMessage = form.errorMessage;
  } else if (form.status === 'disconnected') {
    status = ConnectionStatus.disconnected;
    statusMessage = 'This form has been disconnected.';
  }
  return toIntegrationConnection(integration, {
    id: `${form.formType}_${form.formId}`,
    name: form.name,
    dataFlow: DataFlow.in,
    path: 'integrations.forms.form_details',
    models: [FORM_URL_TYPE[form.formType] ?? form.formType, form.id],
    dynamicSegment: undefined,
    createdAt: form.createdAt,
    lastActivity: form.lastActivityAt,
    status,
    statusMessage,
  });
}

/** Custom (scanned / API) forms → connections. */
export function parseCustomForms(forms: readonly FormSource[]): IntegrationConnection[] {
  return forms
    .filter((form) => form.isScannedForm || form.isApiForm)
    .map((form) => parseForm(CUSTOM_FORMS_INTEGRATION, form));
}

/** Typeform forms → connections. */
export function parseTypeForms(forms: readonly FormSource[]): IntegrationConnection[] {
  const metadata = getStaticIntegrations();
  const integration: ConnectionInput = {
    id: metadata.typeform.id,
    name: metadata.typeform.name,
    imageSrc: metadata.typeform.imageSrc,
  };
  return forms.filter((form) => form.isTypeform).map((form) => parseForm(integration, form));
}

/** Jotform forms → connections. */
export function parseJotForms(forms: readonly FormSource[]): IntegrationConnection[] {
  const metadata = getStaticIntegrations();
  const integration: ConnectionInput = {
    id: metadata.jotform.id,
    name: metadata.jotform.name,
    imageSrc: metadata.jotform.imageSrc,
  };
  return forms.filter((form) => form.isJotform).map((form) => parseForm(integration, form));
}

/** Reporting webhooks → connections. */
export function parseReportingWebhooks(sources: ConnectionSources): IntegrationConnection[] {
  return sources.reportingWebhooks.map((webhook) => {
    let status: ConnectionStatus = ConnectionStatus.connected;
    let statusMessage: string | undefined;
    if (!webhook.enabled) {
      if (webhook.events == null || webhook.events.length > 0) {
        status = ConnectionStatus.disabled;
        statusMessage = 'This connection has been disabled.';
      } else {
        status = ConnectionStatus.error;
        statusMessage = 'No events selected. Enable at least one event to send out to activate this connection.';
      }
    }
    return toIntegrationConnection(
      {},
      {
        id: `${IntegrationIDs.reportingWebhooks}_${webhook.id}`,
        // Override the id-based type: this is a reporting-webhook, not the id.
        type: IntegrationIDs.reportingWebhooks,
        icon: 'webhook',
        name: isEmptyString(webhook.name) ? 'Reporting Webhook' : webhook.name,
        dataFlow: DataFlow.out,
        path: 'integrations.reporting-webhooks.edit',
        models: [webhook.id],
        createdAt: webhook.createdAt,
        lastActivity: webhook.lastActivityAt,
        status,
        statusMessage,
      },
    );
  });
}
